import Console from 'utility/Debug/Console';
import URLSearchParamsUtils from 'utility/Provide/Url';
import { callApi } from 'utility/Request/callApi';
import AsyncUtils from 'utility/Wait/Async';

import { ApiEndpoint } from 'types/Request/endpoint';

import { forEachPendingEvent } from './helper/callbacks';
import { processPendingUpdate, processResponse } from './helper/processing';
import { EventConfig, PollingRequest, PollingState } from './types';

const DEFAULT_POLLING_TIMEOUT = 60_000;
const DEFAULT_POLLING_DELAY = 500;

interface PollSearchDOptions {
    url: string | URL;
    requestData: PollingRequest;
    events: EventConfig;
    delay?: number;
    abortController?: AbortController;
    pollingState?: PollingState;
}

export async function pollSearchD({
    url,
    requestData,
    events,
    delay = DEFAULT_POLLING_DELAY,
    abortController,
    pollingState: { started, previousEventData } = {
        started: Date.now(),
        previousEventData: new Map(),
    },
}: PollSearchDOptions): Promise<void> {
    //calculate global timeout as maximum of the effective timeouts of all configured events
    const globalTimeout = Object.values(events).reduce(
        (globalTimeout, { timeout }) =>
            Math.max(globalTimeout, timeout ?? DEFAULT_POLLING_TIMEOUT),
        0
    );
    Console.info('start polling:', {
        url,
        requestData,
        events,
        delay,
        globalTimeout,
    });
    const queryParams = URLSearchParamsUtils.fromObject({ ...requestData });

    // helper function to call onTimeout callback for all events considered pending
    const notifyTimeoutToAllPending = () =>
        forEachPendingEvent(
            events,
            previousEventData,
            (config, previousData) => {
                processPendingUpdate(config, previousData);
                config.onTimeout?.(previousData?.updatePending ?? false);
            }
        );

    // helper function to call onPollError callback for all events considered pending
    const notifyErrorToAllPending = () =>
        forEachPendingEvent(
            events,
            previousEventData,
            (config, previousData) => {
                processPendingUpdate(config, previousData);
                config.onPollError?.(previousData?.updatePending ?? false);
            }
        );

    // internal helper function to repeatedly be called, will return resolve when polling is stopped
    const call = async (): Promise<void> => {
        if (abortController?.signal.aborted) {
            Console.info('polling aborted');
            // abort requested -> skip execution
            return;
        }
        const timePassed = Date.now() - started;
        if (timePassed > globalTimeout) {
            // global timeout reached -> notify all pending events
            Console.info('global poll timeout reached:', {
                globalTimeout,
                timePassed,
                previousEventData,
            });
            return notifyTimeoutToAllPending();
        }
        try {
            const pollingResponse = await callApi(ApiEndpoint.ListPolling, {
                url,
                queryParams,
                abortController,
                requestOptions: {
                    mode: 'cors',
                },
            });
            if (!pollingResponse.parsedBody) {
                Console.error('polling call failed:', pollingResponse);
                return;
            }
            const stillPending = processResponse({
                responseData: pollingResponse.parsedBody.data,
                previousEventData,
                events,
                notifyErrorToAllPending,
                timePassed,
            });
            if (stillPending) {
                Console.info('polling still necessary');
                await AsyncUtils.delay(delay);
                await call();
            }
        } catch (e) {
            Console.error(e);
            return;
        }
    };

    return call();
}
